/**
 * Encounter type dispatcher.
 *
 * Routes a (`patient`, optional `encounter`, optional `activityContext`,
 * optional `restrictToEncounterTypes`) tuple to the typed form component
 * that owns its encounter type. Resolution order:
 *
 *  1. Existing encounter's `encounter-type` tag (edit / draft resume).
 *  2. Activity-context single-string mapping (auto-select, hides Type).
 *  3. Single-element `restrictToEncounterTypes`.
 *  4. Otherwise mount {@link UnifiedTypePickerForm} so the modal renders
 *     a wizard immediately and the picker is its first step. On pick,
 *     the unified form remounts the chosen typed form via
 *     {@link ResolveTypedForm} with a single-element restriction so the
 *     typed form's own Type step stays hidden.
 *
 * @agentic connections
 */

import { Alert, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { forwardRef } from "react";
import type { Encounter } from "@medplum/fhirtypes";
import {
  ACTIVITY_ENCOUNTER_TYPE_MAP,
  isIntakeEncounterType,
  VALUE_SET,
} from "@benchmark/core/constants";
import type { EncounterActivityContext } from "@benchmark/core/types";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";
import type { EncounterFormRef } from "./EncounterFormShell";
import { ResolveTypedForm } from "./resolveTypedForm";
import { UnifiedTypePickerForm } from "./UnifiedTypePickerForm";

const resolveEncounterTypeCode = (
  encounter: Encounter | undefined,
  activityContext: EncounterActivityContext | undefined,
  restrictToEncounterTypes: string[] | undefined,
): string | undefined => {
  if (encounter) {
    const tag = encounter.meta?.tag?.find(
      (t) => t.system === VALUE_SET.ENCOUNTER_TYPE,
    );
    if (tag?.code) return tag.code;
  }
  if (activityContext?.activityCode) {
    const mapped = ACTIVITY_ENCOUNTER_TYPE_MAP[activityContext.activityCode];
    if (typeof mapped === "string") return mapped;
    // All intake tracks share one form; resolve a homogeneous-intake activity
    // (e.g. `complete-intake`) directly to it — the unified intake derives its
    // persisted code from the Schedule Intake billing flag, so no picker.
    if (
      Array.isArray(mapped) &&
      mapped.length > 0 &&
      mapped.every(isIntakeEncounterType)
    ) {
      return mapped[0];
    }
  }
  if (restrictToEncounterTypes && restrictToEncounterTypes.length === 1) {
    return restrictToEncounterTypes[0];
  }
  // A multi-element intake-only restriction also resolves to the unified form.
  if (
    restrictToEncounterTypes &&
    restrictToEncounterTypes.length > 1 &&
    restrictToEncounterTypes.every(isIntakeEncounterType)
  ) {
    return restrictToEncounterTypes[0];
  }
  return undefined;
};

/**
 * Shared props passed from the shell to whichever typed form gets
 * dispatched. Mirrors `EncounterFormProps` minus the URL fallback fields,
 * which the shell resolves before calling the dispatcher.
 *
 * @agentic connections
 */
export type EncounterDispatchProps = {
  patientId?: string;
  encounter?: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: EncounterActivityContext;
  restrictToEncounterTypes?: string[];
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Encounter type dispatcher. See file header for resolution order.
 *
 * @agentic connections
 */
export const EncounterTypeDispatcher = forwardRef<
  EncounterFormRef,
  EncounterDispatchProps
>(function EncounterTypeDispatcher(props, ref) {
  const resolvedType = resolveEncounterTypeCode(
    props.encounter,
    props.activityContext,
    props.restrictToEncounterTypes,
  );

  if (resolvedType) {
    return <ResolveTypedForm ref={ref} pickedType={resolvedType} {...props} />;
  }

  // Editing an existing encounter whose type tag is unrecognised → show an
  // alert. (When `encounter` is set but `resolvedType` is undefined, the
  // tag is missing or unknown.)
  if (props.encounter) {
    return (
      <Stack gap="md" data-testid="encounter-type-dispatcher-error">
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          title="Unable to open this encounter"
        >
          <Text size="sm">
            We could not determine the type of this encounter. Please refresh
            and try again, or contact support if the issue persists.
          </Text>
        </Alert>
      </Stack>
    );
  }

  // Free creation or multi-element restriction → mount the unified picker
  // form so the modal renders the wizard immediately.
  return <UnifiedTypePickerForm ref={ref} {...props} />;
});
